import {defineMessages} from 'react-intl';
import type {Sorting} from '@igp/shared/api';
import {type MapResult, useAutoMapper} from '@igp/shared/auto-mapper';
import type {DebtTransferLog} from '@igp/shared/contract/pam';
import {type AdjustAgentDebtInput, type Timestamp} from '@igp/shared/contract/pam';
import type {UseDownloadProps, UseDownloadResult} from '@igp/shared/redux/download';
import {useDownload} from '@igp/shared/redux/download';
import {useReduxForm} from '@igp/shared/redux/form';
import type {UseListViewEntityProps, UseListViewEntityResult} from '@igp/shared/redux/view';
import {getTodayDateRange} from '@igp/shared/utils';
import {useViewInit} from '@redux';

import {debtTransferLogActions} from './actions';
import {
    AdjustAgentDebtInputTypeName,
    AdjustDebtFormModelTypeName,
    DebtTransferLogQueryFieldsTypeName,
    DebtTransferLogServiceTypes,
    DebtTransferLogSortKeysTypeName,
    DebtTransferLogTypeName,
    DebtTransferLogViewModelKeysTypeName,
    DebtTransferLogViewModelTypeName,
} from './constants';
import type {
    AdjustDebtFormModel,
    DebtTransferLogEntityType,
    DebtTransferLogExtended,
    DebtTransferLogFilterKeys,
    DebtTransferLogQueryFields,
    DebtTransferLogSortKeys,
    DebtTransferLogViewModel,
    DebtTransferLogViewModelKeys,
} from './types';

export const localizedHeaders = defineMessages<DebtTransferLogViewModelKeys>({
    logId: {
        id: 'DebtTransferLogHooks_logId',
        defaultMessage: 'Log ID',
    },
    transferDate: {
        id: 'DebtTransferLogHooks_transferDate',
        defaultMessage: 'Transfer Date',
    },
    agentId: {
        id: 'DebtTransferLogHooks_agentId',
        defaultMessage: 'Agent ID',
    },
    agentUsername: {
        id: 'DebtTransferLogHooks_agentUsername',
        defaultMessage: 'Agent Username',
    },
    masterAgentId: {
        id: 'DebtTransferLogHooks_masterAgentId',
        defaultMessage: 'Master Agent ID',
    },
    masterAgentUsername: {
        id: 'DebtTransferLogHooks_masterAgentUsername',
        defaultMessage: 'Master Agent Username',
    },
    transferredAmount: {
        id: 'DebtTransferLogHooks_transferredAmount',
        defaultMessage: 'Transferred Amount',
    },
    createdAt: {
        id: 'DebtTransferLogHooks_createdAt',
        defaultMessage: 'Created At',
    },
});

type UseDebtTranferLogsProps = Omit<UseListViewEntityProps<DebtTransferLogFilterKeys, DebtTransferLogViewModelKeys>, 'ids'>;

type UseDebtTranferLogsResult = Omit<
    UseListViewEntityResult<DebtTransferLogViewModel, DebtTransferLogViewModelKeys>,
    'requestStatus' | 'forceViewUpdate'
> & {lastUpdated: Timestamp};

export function useDebtTransferLogs({
    viewType,
    displayName,
    cleanDelay,
    isCancelable,
    fields,
    defaultFilters,
    defaultSorting,
    defaultPaging,
    validateFilter,
    syncWithUrl,
    realtimeMode,
    triggers,
}: UseDebtTranferLogsProps): UseDebtTranferLogsResult {
    const mapper = useAutoMapper();

    const queryFields = fields.map(fields =>
        mapper.map<DebtTransferLogViewModelKeys, DebtTransferLogQueryFields>(
            fields,
            DebtTransferLogViewModelKeysTypeName,
            DebtTransferLogQueryFieldsTypeName
        )
    );

    const {items, totalCount, viewEntity, getSearchFilter, handleFilterChange, handlePageChange, handlePageSizeChange, handleSortChange} =
        useViewInit<DebtTransferLogExtended, DebtTransferLogFilterKeys, DebtTransferLogQueryFields, DebtTransferLogSortKeys>({
            viewType,
            displayName,
            cleanDelay,
            isCancelable,
            entity: {
                fields: queryFields,
                entity: 'DebtTransferLog' satisfies DebtTransferLogEntityType,
            },
            realtime: realtimeMode
                ? {
                      entity: 'DebtTransferLog' satisfies DebtTransferLogEntityType,
                      mode: realtimeMode,
                      triggers,
                  }
                : undefined,
            defaultFilters,
            defaultPaging,
            defaultSorting: mapSorting(defaultSorting),
            validateFilter,
            syncWithUrl,
        });
    const lastUpdated = items.find(log => log.is_general)?.last_updated;
    const viewModelItems: MapResult<DebtTransferLogViewModel>[] = items
        .filter(log => !log.is_general)
        .map(log =>
            mapper.tryMap<DebtTransferLog, DebtTransferLogViewModel>(log, DebtTransferLogTypeName, DebtTransferLogViewModelTypeName)
        );

    const searchFilter = getSearchFilter(i =>
        mapper.map<DebtTransferLogSortKeys, DebtTransferLogViewModelKeys>(
            i,
            DebtTransferLogSortKeysTypeName,
            DebtTransferLogViewModelKeysTypeName
        )
    );

    function mapSorting(sortings: Sorting<DebtTransferLogViewModelKeys>[]): Sorting<DebtTransferLogSortKeys>[] {
        return sortings.map(sorting => ({
            field: mapper.map<DebtTransferLogViewModelKeys, DebtTransferLogSortKeys>(
                sorting.field,
                DebtTransferLogViewModelKeysTypeName,
                DebtTransferLogSortKeysTypeName
            ),
            sort: sorting.sort,
        }));
    }

    function handleVmSortChange(newSortings: Sorting<DebtTransferLogViewModelKeys>[]) {
        handleSortChange(mapSorting(newSortings));
    }
    return {
        items: viewModelItems?.map(i => i.model),
        rawItems: viewModelItems?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        totalCount,
        lastUpdated,
        filterString: viewEntity?.filter,
        searchFilter,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange: handleVmSortChange,
    };
}

type UseDebtTransferLogDownloadProps = Pick<
    UseDownloadProps<DebtTransferLogViewModelKeys>,
    'keys' | 'filename' | 'filter' | 'total' | 'mode'
>;

export function useDebtTransferLogDownload(props: UseDebtTransferLogDownloadProps): UseDownloadResult {
    const {handleDownload, isProgress} = useDownload<DebtTransferLogViewModelKeys>({
        ...props,
        strategySymbol:
            props.mode === 'client'
                ? DebtTransferLogServiceTypes.downloadClientStrategy
                : DebtTransferLogServiceTypes.downloadServerStrategy,
        headersLocalization: localizedHeaders,
    });
    return {
        isProgress,
        handleDownload,
    };
}

type UseAdjustDebtFormProps = {
    onSuccess: () => void;
};

export function useAdjustDebtForm({onSuccess}: UseAdjustDebtFormProps) {
    const mapper = useAutoMapper();
    const form = useReduxForm<AdjustDebtFormModel, AdjustAgentDebtInput>({
        asyncAction: debtTransferLogActions.adjustAgentDebt,
        initialModel: {
            adjustType: 'Deduct',
            transferDate: {
                seconds: getTodayDateRange()[0],
            },
        },
        onSuccess,
        map: formModel =>
            mapper.map<AdjustDebtFormModel, AdjustAgentDebtInput>(formModel, AdjustDebtFormModelTypeName, AdjustAgentDebtInputTypeName),
        mode: 'onChange',
        shouldResetOnError: false,
    });

    return form;
}
